/** Matches `USERNAME_MAX_LEN` in the account name rules. */
const NAME_MAX = 16

/** Character before `@` that may start a tag. */
const BEFORE = /[\s([{"']/

/** Character after a full name that ends the tag. */
const AFTER = /[\s.,!?;:)\]}"']/

export type ChatTextPart =
  | { kind: 'text'; text: string }
  | { kind: 'mention'; username: string; text: string }

/**
 * Online names, longest first, so "Ana Maria" wins over "Ana".
 * Drops blanks, `@`, and anything past the name limit.
 */
function rosterOf(online: readonly string[]): string[] {
  const seen = new Set<string>()
  const names: string[] = []
  for (const raw of online) {
    if (typeof raw !== 'string') continue
    const name = raw.replace(/\s+/g, ' ').trim()
    if (!name || name.includes('@') || name.length > NAME_MAX) continue
    const key = name.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    names.push(name)
  }
  names.sort((a, b) => b.length - a.length || a.localeCompare(b))
  return names
}

function startsWithName(rest: string, name: string): boolean {
  return rest.toLowerCase().startsWith(name.toLowerCase())
}

/** Full name, ignoring case and surrounding spaces. */
export function chatNamesMatch(left: string, right: string): boolean {
  return left.trim().toLowerCase() === right.trim().toLowerCase()
}

/**
 * Replaces `@previous` with `@next` using the same tag boundaries as
 * `splitChatMentions`. A longer name that only starts with `previous` stays.
 */
export function renameMentionText(text: string, previous: string, next: string): string {
  if (!previous || previous === next) return text
  let out = ''
  let i = 0
  while (i < text.length) {
    const atBoundary = i === 0 || BEFORE.test(text[i - 1] ?? '')
    if (text[i] === '@' && atBoundary) {
      const rest = text.slice(i + 1)
      if (startsWithName(rest, previous)) {
        const nextChar = rest[previous.length]
        if (nextChar === undefined || AFTER.test(nextChar)) {
          out += `@${next}`
          i += 1 + previous.length
          continue
        }
      }
    }
    out += text[i]
    i += 1
  }
  return out
}

/**
 * Author label and `@previous` tags on one stored line.
 * Returns the same object when nothing changed.
 */
export function applyChatRename<
  T extends { username: string; text: string; mentions?: string[] },
>(line: T, previous: string, next: string): T {
  if (!previous || previous === next) return line
  const username = chatNamesMatch(line.username, previous) ? next : line.username
  const text = renameMentionText(line.text, previous, next)
  let mentions = line.mentions
  let mentionsChanged = false
  if (mentions) {
    const mapped = mentions.map((name) => {
      if (!chatNamesMatch(name, previous)) return name
      mentionsChanged = true
      return next
    })
    if (mentionsChanged) mentions = mapped
  }
  if (username === line.username && text === line.text && !mentionsChanged) return line
  return {
    ...line,
    username,
    text,
    ...(mentionsChanged ? { mentions } : {}),
  }
}

/**
 * Splits chat text into plain runs and `@Name` tags.
 * A tag exists only when `name` is in `online` (the connected roster).
 */
export function splitChatMentions(
  text: string,
  online: readonly string[],
): ChatTextPart[] {
  const roster = rosterOf(online)
  const parts: ChatTextPart[] = []
  let buf = ''
  const flush = (): void => {
    if (!buf) return
    parts.push({ kind: 'text', text: buf })
    buf = ''
  }
  let i = 0
  while (i < text.length) {
    const atBoundary = i === 0 || BEFORE.test(text[i - 1] ?? '')
    if (text[i] === '@' && atBoundary) {
      const rest = text.slice(i + 1)
      const hit = roster.find((name) => {
        if (!startsWithName(rest, name)) return false
        const next = rest[name.length]
        return next === undefined || AFTER.test(next)
      })
      if (hit) {
        flush()
        parts.push({ kind: 'mention', username: hit, text: `@${hit}` })
        i += 1 + hit.length
        continue
      }
    }
    buf += text[i]
    i += 1
  }
  flush()
  return parts
}

/**
 * Rewrites matched tags to the account's exact name.
 * Unmatched `@text` stays literal and is not a tag.
 */
export function canonicalizeMentions(
  text: string,
  online: readonly string[],
): { text: string; mentions: string[] } {
  const parts = splitChatMentions(text, online)
  const mentions: string[] = []
  let out = ''
  for (const part of parts) {
    if (part.kind === 'mention') {
      if (!mentions.includes(part.username)) mentions.push(part.username)
      out += part.text
    } else {
      out += part.text
    }
  }
  return { text: out, mentions }
}

/**
 * The `@query` the caret is still editing, or null when `@` is not active.
 */
export function activeMentionQuery(
  text: string,
  cursor: number,
): { start: number; query: string } | null {
  if (cursor < 0 || cursor > text.length) return null
  const upto = text.slice(0, cursor)
  const at = upto.lastIndexOf('@')
  if (at < 0) return null
  if (at > 0 && !BEFORE.test(upto[at - 1] ?? '')) return null
  const query = upto.slice(at + 1)
  if (query.length > NAME_MAX) return null
  return { start: at, query }
}

/**
 * Connected names that start with `query`, excluding `self`. At most eight.
 */
export function mentionSuggestions(
  query: string,
  online: readonly string[],
  self?: string,
): string[] {
  const q = query.toLowerCase()
  const skip = self?.trim().toLowerCase() ?? ''
  return rosterOf(online)
    .filter((name) => {
      if (skip && name.toLowerCase() === skip) return false
      return name.toLowerCase().startsWith(q)
    })
    .sort((a, b) => a.localeCompare(b))
    .slice(0, 8)
}
